// Tool Agent — RAG-based tool discovery + single-turn LLM execution

const SYSTEM_PROMPT = `You are a tool-calling agent. The user's request has been matched to the tools below.
Select the correct tool and set its parameters based on the user's intent.
If no tool matches, reply with text explaining what you can't do.`

export function createToolAgent({ registry, llmCall, buildCtx }) {
  async function execute(_, message) {
    console.log('[Tool Agent] query:', message)
    console.log('[Tool Agent] registry size:', registry.tools.size)
    console.log('[Tool Agent] registered tools:', [...registry.tools.keys()])
    console.log('[Tool Agent] prefixes:', { queryPrefix: registry.queryPrefix, docPrefix: registry.docPrefix })
    const allMatches = await registry.search(message, { limit: 100, minScore: 0 })
    console.log('[Tool Agent] all scores:', allMatches.map(m => ({ id: m.entry.id, score: m.score.toFixed(3) })))
    const matches = allMatches.slice(0, 5)
    console.log('[Tool Agent] matches:', matches.map(m => ({ id: m.entry.id, score: m.score.toFixed(3) })))

    if (!matches.length) {
      return 'No matching tools found for this request.'
    }

    // Build tool definitions for LLM
    const idMap = {}
    const toolDefs = matches.map(m => {
      const safeName = m.entry.id.replace(/\//g, '_')
      idMap[safeName] = m.entry.id
      idMap[m.entry.id] = m.entry.id
      return {
        name: safeName,
        description: m.entry.description,
        parameters: m.entry.parameters,
      }
    })

    // Build messages with examples as context
    const exampleText = matches
      .filter(m => m.entry.examples?.length)
      .map(m => {
        const exLines = m.entry.examples.map(ex =>
          `  User: "${ex.input}" → ${m.entry.id}(${JSON.stringify(ex.args)})`
        ).join('\n')
        return `${m.entry.id}:\n${exLines}`
      })
      .join('\n\n')

    const messages = [
      { role: 'system', content: SYSTEM_PROMPT },
    ]
    if (exampleText) {
      messages.push({ role: 'system', content: `Examples:\n${exampleText}` })
    }
    messages.push({ role: 'user', content: message })

    const response = await llmCall(messages, toolDefs)

    if (!response.toolCalls?.length) {
      return response.answer || 'Could not determine which tool to use.'
    }

    const ctx = buildCtx ? await buildCtx() : {}
    const results = await Promise.all(
      response.toolCalls.map(async tc => {
        const args = typeof tc.function.arguments === 'string'
          ? JSON.parse(tc.function.arguments)
          : tc.function.arguments || {}
        const toolId = idMap[tc.function.name]
        if (!toolId) {
          return { tool: tc.function.name, error: `Unknown tool: ${tc.function.name}` }
        }
        console.log('[Tool Agent] calling:', toolId, 'args:', JSON.stringify(args))
        return { tool: toolId, result: await registry.execute(toolId, ctx, args) }
      })
    )

    return JSON.stringify({ results })
  }

  function getToolList() {
    return registry.list()
  }

  function getSystemPrompt() {
    const toolSummary = registry.list().map(t => `- ${t.id}: ${t.description}`).join('\n')
    return `${SYSTEM_PROMPT}\n\nRegistered tools:\n${toolSummary}`
  }

  function getDescription() {
    return 'Natural language tool agent with RAG-based tool discovery.'
  }

  return { execute, getToolList, getSystemPrompt, getDescription }
}
